/**
 * Hook to register native push notifications for drivers and admins after login.
 * Requests permission with a brief delay so the user sees the dashboard first,
 * then registers the device token with the backend.
 * Re-registers on every app resume to ensure tokens stay fresh.
 * Cleans up token on logout via auth state listener.
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { isNativePushAvailable, registerNativePush, unregisterPushToken } from '@/lib/nativePushNotifications';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

const PUSH_PROMPT_DELAY_MS = 3000; // 3s after dashboard loads

export function useNativePushRegistration() {
  const { status } = useAuth();
  const { isDriver, isAdmin, loading: roleLoading } = useUserRole();
  const registered = useRef(false);

  // Register push notifications when authed + role resolved
  useEffect(() => {
    if (status !== 'authed' || roleLoading) return;
    if (!isNativePushAvailable()) return;
    // Register for both drivers and admins
    if (!isDriver && !isAdmin) return;

    const doRegister = async () => {
      console.log('[NativePush] Requesting permission for', isDriver ? 'driver' : 'admin');
      const success = await registerNativePush();
      if (success) {
        registered.current = true;
        console.log('[NativePush] Registration complete');
      } else {
        console.warn('[NativePush] Registration failed or permission denied');
      }
    };

    // Initial registration with delay
    const timer = setTimeout(doRegister, registered.current ? 0 : PUSH_PROMPT_DELAY_MS);

    // Re-register when app resumes (token may have changed)
    let resumeListener: { remove: () => void } | null = null;
    if (Capacitor.isNativePlatform()) {
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive && registered.current) {
          console.log('[NativePush] App resumed, re-registering token');
          registerNativePush();
        }
      }).then(h => { resumeListener = h; });
    }

    return () => {
      clearTimeout(timer);
      resumeListener?.remove();
    };
  }, [status, isDriver, isAdmin, roleLoading]);

  // Clean up push token on logout
  useEffect(() => {
    if (!isNativePushAvailable()) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT' && registered.current) {
        console.log('[NativePush] User signed out, cleaning up push token');
        unregisterPushToken();
        registered.current = false;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);
}
